import { useEffect, useState } from 'react';
import { GripVertical } from 'lucide-react';
import { ncfAPI } from '../../services/api';

const FORMATS = ['text', 'email', 'tel', 'number', 'currency', 'date', 'boolean', 'address'];
const OPERATORS = [
  ['equals', 'equals'],
  ['notEquals', 'does not equal'],
  ['in', 'is one of'],
  ['notIn', 'is not one of'],
  ['isSet', 'has a value'],
  ['isNotSet', 'is blank']
];
const emptyCondition = () => ({ sourcePath: '', operator: 'equals', value: '' });
const emptyRules = () => ({ version: 1, any: [{ all: [emptyCondition()] }] });

const initialField = {
  sourcePath: '',
  label: '',
  fieldGroup: '',
  format: 'text',
  isRequired: false,
  options: '',
  isMultiline: false
};

const initialEditModal = {
  show: false,
  fieldId: null,
  label: '',
  sourcePath: '',
  fieldGroup: '',
  format: 'text',
  requirementMode: 'optional',
  requirementRules: null,
  options: '',
  isMultiline: false
};

const initialSectionModal = {
  show: false,
  sectionName: '',
  visibilityMode: 'always',
  visibilityRules: emptyRules()
};

function NcfFieldInventoryAdmin() {
  const [fields, setFields] = useState([]);
  const [sectionVisibility, setSectionVisibility] = useState([]);
  const [draft, setDraft] = useState(initialField);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [assignmentFailures, setAssignmentFailures] = useState([]);
  const [editModal, setEditModal] = useState(initialEditModal);
  const [sectionModal, setSectionModal] = useState(initialSectionModal);
  const [rebuildingAssignments, setRebuildingAssignments] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [draggedFieldId, setDraggedFieldId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const load = async () => {
    try {
      const response = await ncfAPI.adminFields();
      setFields(response.data.fields || []);
      setSectionVisibility(response.data.sectionVisibility || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not load NCF field inventory.');
    }
  };

  useEffect(() => { load(); }, []);

  const setDraftField = (name, value) => setDraft(previous => ({ ...previous, [name]: value }));

  const addField = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    if (!draft.fieldGroup) {
      setError('Select the form section where this field should appear.');
      return;
    }
    const sectionFields = fields.filter(field => field.isActive && field.fieldGroup === draft.fieldGroup);
    const sortOrder = Math.max(0, ...sectionFields.map(field => Number(field.sortOrder) || 0)) + 10;
    try {
      await ncfAPI.createField({ ...draft, sortOrder });
      setDraft(initialField);
      await load();
      setMessage('Field added.');
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not add field.');
    }
  };

  const updateField = async (fieldId, payload) => {
    setError('');
    setMessage('');
    try {
      await ncfAPI.updateFieldDefinition(fieldId, payload);
      await load();
      setMessage('Field updated.');
      return true;
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not update field.');
      return false;
    }
  };

  const openEditModal = (field) => setEditModal({
    show: true,
    fieldId: field.fieldId,
    label: field.label,
    sourcePath: field.sourcePath,
    fieldGroup: field.fieldGroup || '',
    format: field.format || 'text',
    requirementMode: field.requirementRules ? 'conditional' : field.isRequired ? 'always' : 'optional',
    requirementRules: field.requirementRules || null,
    options: (field.options || []).join(', '),
    isMultiline: Boolean(field.isMultiline)
  });

  const setEditModalField = (name, value) => setEditModal(previous => ({ ...previous, [name]: value }));

  const setRequirementMode = (requirementMode) => setEditModal(previous => ({
    ...previous,
    requirementMode,
    requirementRules: requirementMode === 'conditional' ? previous.requirementRules || emptyRules() : null
  }));

  const updateCondition = (groupIndex, conditionIndex, patch) => setEditModal(previous => ({
    ...previous,
    requirementRules: {
      ...previous.requirementRules,
      any: previous.requirementRules.any.map((group, currentGroupIndex) => currentGroupIndex !== groupIndex ? group : ({
        all: group.all.map((condition, currentConditionIndex) => currentConditionIndex === conditionIndex
          ? { ...condition, ...patch }
          : condition)
      }))
    }
  }));

  const addCondition = (groupIndex) => setEditModal(previous => ({
    ...previous,
    requirementRules: {
      ...previous.requirementRules,
      any: previous.requirementRules.any.map((group, currentGroupIndex) => currentGroupIndex === groupIndex
        ? { all: [...group.all, emptyCondition()] }
        : group)
    }
  }));

  const removeCondition = (groupIndex, conditionIndex) => setEditModal(previous => ({
    ...previous,
    requirementRules: {
      ...previous.requirementRules,
      any: previous.requirementRules.any.map((group, currentGroupIndex) => currentGroupIndex === groupIndex
        ? { all: group.all.filter((_, currentConditionIndex) => currentConditionIndex !== conditionIndex) }
        : group).filter(group => group.all.length > 0)
    }
  }));

  const addRuleGroup = () => setEditModal(previous => ({
    ...previous,
    requirementRules: { ...previous.requirementRules, any: [...previous.requirementRules.any, { all: [emptyCondition()] }] }
  }));

  const removeRuleGroup = (groupIndex) => setEditModal(previous => ({
    ...previous,
    requirementRules: { ...previous.requirementRules, any: previous.requirementRules.any.filter((_, index) => index !== groupIndex) }
  }));

  const saveEditModal = async () => {
    if (!editModal.label.trim() || !editModal.sourcePath.trim()) {
      setError('Label and source path are required.');
      return;
    }
    const trimmedOptions = editModal.options.trim();
    const saved = await updateField(editModal.fieldId, {
      label: editModal.label.trim(),
      sourcePath: editModal.sourcePath.trim(),
      fieldGroup: editModal.fieldGroup.trim() || null,
      format: editModal.format,
      isRequired: editModal.requirementMode === 'always',
      requirementRules: editModal.requirementMode === 'conditional' ? editModal.requirementRules : null,
      options: trimmedOptions,
      clearOptions: !trimmedOptions,
      isMultiline: editModal.isMultiline
    });
    if (saved) setEditModal(initialEditModal);
  };

  const openSectionModal = (sectionName) => {
    const saved = sectionVisibility.find(item => item.sectionName === sectionName);
    setSectionModal({
      show: true,
      sectionName,
      visibilityMode: saved?.visibilityMode || 'always',
      visibilityRules: saved?.visibilityRules || emptyRules()
    });
  };

  const updateSectionCondition = (groupIndex, conditionIndex, patch) => setSectionModal(previous => ({
    ...previous,
    visibilityRules: {
      ...previous.visibilityRules,
      any: previous.visibilityRules.any.map((group, currentGroupIndex) => currentGroupIndex !== groupIndex ? group : ({
        all: group.all.map((condition, currentConditionIndex) => currentConditionIndex === conditionIndex
          ? { ...condition, ...patch }
          : condition)
      }))
    }
  }));

  const addSectionCondition = (groupIndex) => setSectionModal(previous => ({
    ...previous,
    visibilityRules: {
      ...previous.visibilityRules,
      any: previous.visibilityRules.any.map((group, currentGroupIndex) => currentGroupIndex === groupIndex
        ? { all: [...group.all, emptyCondition()] }
        : group)
    }
  }));

  const removeSectionCondition = (groupIndex, conditionIndex) => setSectionModal(previous => ({
    ...previous,
    visibilityRules: {
      ...previous.visibilityRules,
      any: previous.visibilityRules.any.map((group, currentGroupIndex) => currentGroupIndex === groupIndex
        ? { all: group.all.filter((_, currentConditionIndex) => currentConditionIndex !== conditionIndex) }
        : group).filter(group => group.all.length > 0)
    }
  }));

  const addSectionRuleGroup = () => setSectionModal(previous => ({
    ...previous,
    visibilityRules: { ...previous.visibilityRules, any: [...previous.visibilityRules.any, { all: [emptyCondition()] }] }
  }));

  const removeSectionRuleGroup = (groupIndex) => setSectionModal(previous => ({
    ...previous,
    visibilityRules: { ...previous.visibilityRules, any: previous.visibilityRules.any.filter((_, index) => index !== groupIndex) }
  }));

  const saveSectionModal = async () => {
    setError('');
    setMessage('');
    try {
      await ncfAPI.updateSectionVisibility({
        sectionName: sectionModal.sectionName,
        visibilityMode: sectionModal.visibilityMode,
        visibilityRules: sectionModal.visibilityMode === 'always' ? null : sectionModal.visibilityRules
      });
      await load();
      setSectionModal(initialSectionModal);
      setMessage(`Visibility updated for ${sectionModal.sectionName}.`);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not update section visibility.');
    }
  };

  const rebuildRoleAssignments = async () => {
    if (!window.confirm('Rebuild recruiter, salesperson, and additional reviewer History access for all existing NCFs?')) return;
    setRebuildingAssignments(true);
    setError('');
    setAssignmentFailures([]);
    setMessage('Starting assigned-NCF index rebuild...');
    let afterNcfId = 0;
    let processed = 0;
    let failed = 0;
    const failures = [];
    try {
      let result;
      do {
        const response = await ncfAPI.rebuildNcfRoleAssignments({ afterNcfId, batchSize: 200 });
        result = response.data.result;
        processed += result.processed || 0;
        failed += result.failedCount || 0;
        failures.push(...(result.failures || []));
        afterNcfId = result.nextAfterNcfId || afterNcfId;
        setMessage(`Rebuilt assignments for ${processed} NCFs...`);
      } while (result.hasMore);
      setMessage(`Assignment index rebuild complete: ${processed} NCFs processed, ${failed} failed.`);
      setAssignmentFailures(failures);
    } catch (err) {
      setError(err?.response?.data?.error || `Assignment rebuild stopped after ${processed} NCFs.`);
      setAssignmentFailures(failures);
    } finally {
      setRebuildingAssignments(false);
    }
  };

  const dependencyLabel = (sourcePath) => fields.find(item => item.sourcePath === sourcePath)?.label || sourcePath;
  const conditionSummary = (condition) => {
    const operator = OPERATORS.find(([value]) => value === condition.operator)?.[1] || condition.operator;
    const value = condition.values?.join(', ') || condition.value || '';
    return `${dependencyLabel(condition.sourcePath)} ${operator}${value ? ` ${value}` : ''}`;
  };
  const ruleSummary = (rules) => rules?.any?.map(group => `(${group.all.map(conditionSummary).join(' AND ')})`).join(' OR ') || '';
  const sectionVisibilitySummary = (sectionName) => {
    const setting = sectionVisibility.find(item => item.sectionName === sectionName);
    if (!setting || setting.visibilityMode === 'always') return 'Always shown';
    const action = setting.visibilityMode === 'hideWhen' ? 'Hidden when' : 'Shown when';
    return `${action} ${ruleSummary(setting.visibilityRules)}`;
  };

  const activeFields = fields.filter(field => field.isActive);
  const removedFields = fields.filter(field => !field.isActive);
  const sections = Array.from(new Set(activeFields.map(item => item.fieldGroup).filter(Boolean)));
  const configuredSections = Array.from(new Set(fields.map(item => item.fieldGroup).filter(Boolean)));
  const availableSections = configuredSections.length > 0 ? configuredSections : ['Custom'];

  const finishDragging = () => { setDraggedFieldId(null); setDropTarget(null); };

  const dropField = async (event, targetField, section) => {
    event.preventDefault();
    const draggedId = Number(draggedFieldId);
    if (!draggedId || draggedId === targetField.fieldId || reordering) { finishDragging(); return; }
    const sectionFields = fields.filter(field => field.isActive && field.fieldGroup === section);
    const draggedField = sectionFields.find(field => field.fieldId === draggedId);
    if (!draggedField) { finishDragging(); return; }
    const reordered = sectionFields.filter(field => field.fieldId !== draggedId);
    const targetIndex = reordered.findIndex(field => field.fieldId === targetField.fieldId);
    const insertIndex = targetIndex + (dropTarget?.position === 'after' ? 1 : 0);
    reordered.splice(insertIndex, 0, draggedField);
    const sortOrders = sectionFields.map(field => Number(field.sortOrder) || 0).sort((left, right) => left - right);
    finishDragging();
    setReordering(true); setError(''); setMessage('');
    try {
      await Promise.all(reordered.map((field, index) => ncfAPI.updateFieldDefinition(field.fieldId, { sortOrder: sortOrders[index] })));
      await load();
      setMessage(`Field order updated in ${section}.`);
    } catch (err) {
      await load();
      setError(err?.response?.data?.error || 'Could not reorder fields.');
    } finally { setReordering(false); }
  };

  const renderFieldRow = (field, section) => <div
    className={`ncf-field-inventory-row${field.isActive ? '' : ' is-inactive'}${draggedFieldId === field.fieldId ? ' is-dragging' : ''}${dropTarget?.fieldId === field.fieldId ? ` is-drop-${dropTarget.position}` : ''}`}
    key={field.fieldId || field.sourcePath}
    onDragOver={field.isActive && field.fieldId ? (event) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      const bounds = event.currentTarget.getBoundingClientRect();
      setDropTarget({ fieldId: field.fieldId, position: event.clientY < bounds.top + bounds.height / 2 ? 'before' : 'after' });
    } : undefined}
    onDrop={field.isActive && field.fieldId ? event => dropField(event, field, section) : undefined}
  >
    {field.isActive && <span
      className="ncf-field-drag-handle"
      draggable={Boolean(field.fieldId) && !reordering}
      onDragStart={(event) => { event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', String(field.fieldId)); setDraggedFieldId(field.fieldId); }}
      onDragEnd={finishDragging}
      title="Drag to reorder within this section"
      aria-label={`Drag ${field.label} to reorder within ${section}`}
    ><GripVertical size={20} /></span>}
    <span className="ncf-field-inventory-copy"><strong>{field.label}</strong><small>{field.sourcePath} • {field.format}{field.isMultiline && ' • multiline'}{field.options?.length > 0 && ` • options: ${field.options.join(', ')}`}{field.requirementRules && <><br />Required when {ruleSummary(field.requirementRules)}</>}</small></span>
    {field.isActive && (field.requirementRules ? (
      <span className="ncf-conditional-required-badge" title={ruleSummary(field.requirementRules)}>Conditionally required</span>
    ) : (
      <label className="ncf-inline-check"><input type="checkbox" checked={field.isRequired} onChange={event => updateField(field.fieldId, { isRequired: event.target.checked })} disabled={!field.fieldId} /> Required</label>
    ))}
    <span className="ncf-field-actions">{field.isActive && <button className="btn btn-secondary" type="button" onClick={() => openEditModal(field)} disabled={!field.fieldId}>Edit</button>}<button className={field.isActive ? 'btn btn-danger' : 'btn btn-secondary'} type="button" onClick={() => updateField(field.fieldId, { isActive: !field.isActive })} disabled={!field.fieldId}>{field.isActive ? 'Remove' : 'Restore'}</button></span>
  </div>;

  return (
    <section className="ncf-field-inventory-admin">
      <div className="ncf-section-header">
        <div><h2>NCF field inventory</h2><p>Control which fields appear on the candidate Review page.</p></div>
        <button type="button" className="btn btn-secondary" onClick={rebuildRoleAssignments} disabled={rebuildingAssignments}>{rebuildingAssignments ? 'Rebuilding assigned NCF index...' : 'Rebuild assigned NCF index'}</button>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-info">{message}</div>}
      {assignmentFailures.length > 0 && <details className="ncf-assignment-rebuild-failures">
        <summary>Show {assignmentFailures.length} failed NCFs</summary>
        <ul>{assignmentFailures.map((failure, index) => <li key={`${failure.ncfId}-${index}`}>NCF {failure.ncfId}: {failure.error}</li>)}</ul>
      </details>}
      <form className="ncf-field-add" onSubmit={addField}>
        <input value={draft.label} onChange={event => setDraftField('label', event.target.value)} placeholder="Display label" required />
        <input value={draft.sourcePath} onChange={event => setDraftField('sourcePath', event.target.value)} placeholder="Source path, e.g. jobdiva.job.billRate" required />
        <label className="ncf-field-add-section"><span>Form section</span><select value={draft.fieldGroup} onChange={event => setDraftField('fieldGroup', event.target.value)} required><option value="">Select section...</option>{availableSections.map(section => <option key={section} value={section}>{section}</option>)}</select></label>
        <select value={draft.format} onChange={event => setDraftField('format', event.target.value)}>
          {FORMATS.map(format => <option key={format} value={format}>{format === 'tel' ? 'Phone number' : format === 'email' ? 'Email' : format}</option>)}
        </select>
        <label className="ncf-inline-check"><input type="checkbox" checked={draft.isRequired} onChange={event => setDraftField('isRequired', event.target.checked)} /> Required</label>
        <label className="ncf-inline-check"><input type="checkbox" checked={draft.isMultiline} onChange={event => setDraftField('isMultiline', event.target.checked)} /> Multiline (textarea)</label>
        <input value={draft.options} onChange={event => setDraftField('options', event.target.value)} placeholder="Dropdown options, comma-separated (optional)" />
        <button className="btn btn-primary" type="submit">Add field</button>
      </form>
      <datalist id="ncf-field-sections">{availableSections.map(section => <option key={section} value={section} />)}</datalist>
      <div className={`ncf-field-inventory-list${reordering ? ' is-saving-order' : ''}`}>
        {sections.map(section => (
          <div key={section} className="ncf-field-section-group">
            <div className="ncf-field-section-toolbar"><div><h3 className="ncf-field-section-heading">{section}</h3><small>{sectionVisibilitySummary(section)}</small></div><button type="button" className="btn btn-secondary btn-sm" onClick={() => openSectionModal(section)}>Section visibility</button></div>
            {activeFields.filter(field => field.fieldGroup === section).map(field => renderFieldRow(field, section))}
          </div>
        ))}
      </div>
      {removedFields.length > 0 && <details className="ncf-removed-fields">
        <summary>Removed fields <span>{removedFields.length}</span></summary>
        <div className="ncf-field-inventory-list">{removedFields.map(field => renderFieldRow(field, field.fieldGroup))}</div>
      </details>}
      {editModal.show && (
        <div className="modal-overlay" onClick={() => setEditModal(initialEditModal)}>
          <div className="modal-content ncf-field-rule-modal" onClick={event => event.stopPropagation()}>
            <div className="modal-header"><div><h2>Edit field</h2><p>Configure when this field must be completed.</p></div><button type="button" className="close-btn" onClick={() => setEditModal(initialEditModal)}>x</button></div>
            <label className="ncf-field-rule-input">
              <span>Display label</span>
              <input value={editModal.label} onChange={event => setEditModalField('label', event.target.value)} />
            </label>
            <label className="ncf-field-rule-input">
              <span>Source path</span>
              <input value={editModal.sourcePath} onChange={event => setEditModalField('sourcePath', event.target.value)} />
            </label>
            <label className="ncf-field-rule-input">
              <span>Section</span>
              <input value={editModal.fieldGroup} onChange={event => setEditModalField('fieldGroup', event.target.value)} list="ncf-field-sections" />
            </label>
            <label className="ncf-field-rule-input">
              <span>Field type</span>
              <select value={editModal.format} onChange={event => setEditModalField('format', event.target.value)}>
                {FORMATS.map(format => <option key={format} value={format}>{format === 'tel' ? 'Phone number' : format === 'email' ? 'Email' : format}</option>)}
              </select>
            </label>
            <label className="ncf-field-rule-input">
              <span>Dropdown options (comma-separated, optional)</span>
              <input value={editModal.options} onChange={event => setEditModalField('options', event.target.value)} placeholder="e.g. Yes, No" />
            </label>
            <label className="ncf-inline-check ncf-field-rule-multiline">
              <input type="checkbox" checked={editModal.isMultiline} onChange={event => setEditModalField('isMultiline', event.target.checked)} /> Multiline (textarea)
            </label>
            <fieldset className="ncf-requirement-mode"><legend>Requirement</legend>{[['optional', 'Optional'], ['always', 'Always required'], ['conditional', 'Required when conditions match']].map(([value, label]) => <label key={value}><input type="radio" name="requirement-mode" checked={editModal.requirementMode === value} onChange={() => setRequirementMode(value)} /> {label}</label>)}</fieldset>
            {editModal.requirementMode === 'conditional' && <div className="ncf-rule-builder">
              {editModal.requirementRules.any.map((group, groupIndex) => <div className="ncf-rule-group" key={groupIndex}>
                <div className="ncf-rule-group-header"><strong>{groupIndex === 0 ? 'When all are true' : 'Or when all are true'}</strong>{editModal.requirementRules.any.length > 1 && <button type="button" className="btn btn-link" onClick={() => removeRuleGroup(groupIndex)}>Remove group</button>}</div>
                {group.all.map((condition, conditionIndex) => {
                  const controllingField = fields.find(field => field.sourcePath === condition.sourcePath);
                  const noValue = ['isSet', 'isNotSet'].includes(condition.operator);
                  const multipleValues = ['in', 'notIn'].includes(condition.operator);
                  const choices = controllingField?.format === 'boolean' ? ['Yes', 'No'] : controllingField?.options;
                  const conditionValue = multipleValues ? (condition.values || []).join(', ') : condition.value || '';
                  return <div className="ncf-rule-condition" key={conditionIndex}>
                    <select value={condition.sourcePath} onChange={event => updateCondition(groupIndex, conditionIndex, { sourcePath: event.target.value, value: '', values: undefined })}><option value="">Select field...</option>{fields.filter(field => field.isActive && field.sourcePath !== editModal.sourcePath).map(field => <option key={field.sourcePath} value={field.sourcePath}>{field.label}</option>)}</select>
                    <select value={condition.operator} onChange={event => updateCondition(groupIndex, conditionIndex, { operator: event.target.value, value: '', values: undefined })}>{OPERATORS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
                    {!noValue && choices?.length > 0 && !multipleValues ? <select value={conditionValue} onChange={event => updateCondition(groupIndex, conditionIndex, { value: event.target.value })}><option value="">Select...</option>{choices.map(choice => <option key={choice} value={choice}>{choice}</option>)}</select> : !noValue && <input value={conditionValue} onChange={event => updateCondition(groupIndex, conditionIndex, multipleValues ? { values: event.target.value.split(',').map(value => value.trim()).filter(Boolean) } : { value: event.target.value })} placeholder={multipleValues ? 'Value 1, Value 2' : 'Value'} />}
                    <button type="button" className="btn btn-link" onClick={() => removeCondition(groupIndex, conditionIndex)} disabled={group.all.length === 1 && editModal.requirementRules.any.length === 1}>Remove</button>
                  </div>;
                })}
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => addCondition(groupIndex)}>Add AND condition</button>
              </div>)}
              <button type="button" className="btn btn-secondary" onClick={addRuleGroup}>Add OR group</button>
              <p className="ncf-rule-preview"><strong>Preview:</strong> {ruleSummary(editModal.requirementRules)}</p>
            </div>}
            <div className="modal-actions">
              <button className="btn btn-secondary" type="button" onClick={() => setEditModal(initialEditModal)}>Cancel</button>
              <button className="btn btn-primary" type="button" onClick={saveEditModal}>Save</button>
            </div>
          </div>
        </div>
      )}
      {sectionModal.show && <div className="modal-overlay" onClick={() => setSectionModal(initialSectionModal)}>
        <div className="modal-content ncf-field-rule-modal" onClick={event => event.stopPropagation()}>
          <div className="modal-header"><div><h2>Section visibility</h2><p>{sectionModal.sectionName}</p></div><button type="button" className="close-btn" onClick={() => setSectionModal(initialSectionModal)}>x</button></div>
          <fieldset className="ncf-requirement-mode"><legend>Display this section</legend>{[['always', 'Always shown'], ['showWhen', 'Show when conditions match'], ['hideWhen', 'Hide when conditions match']].map(([value, label]) => <label key={value}><input type="radio" name="section-visibility-mode" checked={sectionModal.visibilityMode === value} onChange={() => setSectionModal(previous => ({ ...previous, visibilityMode: value }))} /> {label}</label>)}</fieldset>
          {sectionModal.visibilityMode !== 'always' && <div className="ncf-rule-builder">
            {sectionModal.visibilityRules.any.map((group, groupIndex) => <div className="ncf-rule-group" key={groupIndex}>
              <div className="ncf-rule-group-header"><strong>{groupIndex === 0 ? 'When all are true' : 'Or when all are true'}</strong>{sectionModal.visibilityRules.any.length > 1 && <button type="button" className="btn btn-link" onClick={() => removeSectionRuleGroup(groupIndex)}>Remove group</button>}</div>
              {group.all.map((condition, conditionIndex) => {
                const controllingField = fields.find(field => field.sourcePath === condition.sourcePath);
                const noValue = ['isSet', 'isNotSet'].includes(condition.operator);
                const multipleValues = ['in', 'notIn'].includes(condition.operator);
                const choices = controllingField?.format === 'boolean' ? ['Yes', 'No'] : controllingField?.options;
                const conditionValue = multipleValues ? (condition.values || []).join(', ') : condition.value || '';
                return <div className="ncf-rule-condition" key={conditionIndex}>
                  <select value={condition.sourcePath} onChange={event => updateSectionCondition(groupIndex, conditionIndex, { sourcePath: event.target.value, value: '', values: undefined })}><option value="">Select field...</option>{fields.filter(field => field.isActive).map(field => <option key={field.sourcePath} value={field.sourcePath}>{field.label}</option>)}</select>
                  <select value={condition.operator} onChange={event => updateSectionCondition(groupIndex, conditionIndex, { operator: event.target.value, value: '', values: undefined })}>{OPERATORS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
                  {!noValue && choices?.length > 0 && !multipleValues ? <select value={conditionValue} onChange={event => updateSectionCondition(groupIndex, conditionIndex, { value: event.target.value })}><option value="">Select...</option>{choices.map(choice => <option key={choice} value={choice}>{choice}</option>)}</select> : !noValue && <input value={conditionValue} onChange={event => updateSectionCondition(groupIndex, conditionIndex, multipleValues ? { values: event.target.value.split(',').map(value => value.trim()).filter(Boolean) } : { value: event.target.value })} placeholder={multipleValues ? 'Value 1, Value 2' : 'Value'} />}
                  <button type="button" className="btn btn-link" onClick={() => removeSectionCondition(groupIndex, conditionIndex)} disabled={group.all.length === 1 && sectionModal.visibilityRules.any.length === 1}>Remove</button>
                </div>;
              })}
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => addSectionCondition(groupIndex)}>Add AND condition</button>
            </div>)}
            <button type="button" className="btn btn-secondary" onClick={addSectionRuleGroup}>Add OR group</button>
            <p className="ncf-rule-preview"><strong>Preview:</strong> {ruleSummary(sectionModal.visibilityRules)}</p>
          </div>}
          <div className="modal-actions"><button className="btn btn-secondary" type="button" onClick={() => setSectionModal(initialSectionModal)}>Cancel</button><button className="btn btn-primary" type="button" onClick={saveSectionModal}>Save visibility</button></div>
        </div>
      </div>}
    </section>
  );
}

export default NcfFieldInventoryAdmin;
